/**
 * 🔄 Offline Sync Engine
 * Handles transaction synchronization with dependency resolution and conflict detection
 */

import axios from 'axios';
import db from './db';

class SyncEngine {
  constructor() {
    this.isSyncing = false;
    this.syncListeners = new Set();
    this.maxRetries = 3;
    this.retryDelay = 2000;
  }

  /**
   * Subscribe to sync events
   */
  subscribe(callback) {
    this.syncListeners.add(callback);
    return () => this.syncListeners.delete(callback);
  }

  /**
   * Notify all listeners
   */
  notify(event) {
    this.syncListeners.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        console.error('Error in sync listener:', error);
      }
    });
  }

  /**
   * Main sync function - processes all pending transactions
   * @param {boolean} silentMode - If true, don't notify listeners (no UI refresh)
   */
  async syncAll(silentMode = false) {
    if (this.isSyncing) {
      console.log('⏳ Sync already in progress');
      return { success: false, message: 'Sync already in progress' };
    }

    if (!navigator.onLine) {
      console.log('📴 Cannot sync while offline');
      return { success: false, message: 'Device is offline' };
    }

    this.isSyncing = true;
    this.silentMode = silentMode; // Store for use in notify
    
    if (!silentMode) {
      this.notify({ type: 'sync_started' });
    }

    try {
      console.log('🔄 Starting sync process...');
      
      // STEP 1: Sync pending shift ends FIRST (critical for accounting)
      const shiftEndResult = await this.syncPendingShiftEnds();
      if (!shiftEndResult.success && shiftEndResult.hasPending) {
        console.log('⚠️ Shift end sync failed, stopping sync process');
        this.notify({ 
          type: 'sync_completed', 
          successCount: 0, 
          failCount: 1, 
          allSuccess: false,
          shiftEndPending: true 
        });
        return { 
          success: false, 
          successCount: 0, 
          failCount: 1, 
          message: 'Shift end sync failed - please retry',
          shiftEndPending: true
        };
      }
      
      // STEP 2: Get all pending transactions
      const pendingTransactions = await db.getPendingTransactions();
      
      if (pendingTransactions.length === 0) {
        console.log('✅ No pending transactions to sync');
        this.notify({ type: 'sync_completed', successCount: 0, failCount: 0, allSuccess: true });
        return { success: true, successCount: 0, failCount: 0 };
      }

      console.log(`📤 Found ${pendingTransactions.length} pending transactions`);

      // Build dependency chains
      const chains = this.buildDependencyChains(pendingTransactions);
      console.log(`🔗 Built ${chains.length} dependency chains`);

      let successCount = 0;
      let failCount = 0;
      const idMappings = new Map(); // offline ID → server ID

      // Process each chain sequentially
      for (const chain of chains) {
        console.log(`\n🔗 Processing chain with ${chain.length} transactions`);
        
        for (let i = 0; i < chain.length; i++) {
          const txn = chain[i];
          const isLastInChain = (i === chain.length - 1);
          
          // Skip transactions that have exceeded max retries
          if (txn.retryCount >= this.maxRetries) {
            console.log(`  ⏭️ Skipping [${i + 1}/${chain.length}]: ${txn.endpoint} - Max retries (${this.maxRetries}) exceeded (attempt ${txn.retryCount})`);
            // Delete stale transactions that can't be synced
            await db.deleteTransaction(txn.transactionId);
            console.log(`  🗑️ Deleted stale transaction: ${txn.transactionId}`);
            failCount++;
            continue;
          }
          
          // Skip read operations that shouldn't be in the sync queue
          if (this.isReadOperation(txn.endpoint)) {
            console.log(`  ⏭️ Skipping read operation: ${txn.endpoint}`);
            await db.deleteTransaction(txn.transactionId);
            continue;
          }
          
          try {
            console.log(`  🔄 Syncing [${i + 1}/${chain.length}]: ${txn.method} ${txn.endpoint}`);
            console.log(`  📋 Transaction ID: ${txn.transactionId}, dependsOn: ${txn.dependsOn}`);
            console.log(`  📋 Current ID mappings:`, Object.fromEntries(idMappings));
            
            // Update transaction data with mapped IDs if needed
            const updatedTxn = this.applyIdMappings(txn, idMappings);
            
            // Log the updated data for payment transactions
            if (this.isPayment(txn.endpoint)) {
              const updatedData = typeof updatedTxn.data === 'string' ? JSON.parse(updatedTxn.data) : updatedTxn.data;
              console.log(`  💳 Payment orderId after mapping: ${updatedData?.orderId || updatedData?.id}`);
            }
            
            // Sync the transaction
            const response = await this.syncTransaction(updatedTxn);
            
            // Handle ID mapping for order creation
            if (this.isOrderCreation(txn.endpoint)) {
              const serverId = this.extractServerId(response);
              if (serverId && txn.transactionId) {
                idMappings.set(txn.transactionId, serverId);
                console.log(`  📝 ID Mapping: ${txn.transactionId} → ${serverId}`);
              }
            }
            
            // Mark as synced and clean up (only delete order if last in chain)
            await this.handleSyncSuccess(txn, response, isLastInChain);
            successCount++;
            
            this.notify({
              type: 'sync_progress',
              current: successCount + failCount,
              total: pendingTransactions.length,
              success: successCount,
              failed: failCount
            });
            
            // Small delay between requests
            await this.delay(100);
            
          } catch (error) {
            console.error(`  ❌ Failed to sync transaction:`, error.message);
            console.error(`  ❌ Error details:`, {
              status: error.response?.status,
              statusText: error.response?.statusText,
              data: error.response?.data,
              endpoint: txn.endpoint,
              method: txn.method
            });
            await this.handleSyncError(txn, error);
            failCount++;
          }
        }
      }

      // Clear cache to force refresh (only if not silent and had success)
      if (successCount > 0 && !this.silentMode) {
        await this.clearOrderCache();
      }

      // Clear old conflicts if all synced successfully
      const allSuccess = failCount === 0;
      if (allSuccess) {
        await db.clearAllConflicts();
        console.log('🗑️ Cleared old conflict logs after successful sync');
      }

      console.log(`\n✅ Sync complete: ${successCount} success, ${failCount} failed`);
      
      // Only notify if not in silent mode
      if (!this.silentMode) {
        this.notify({
          type: 'sync_completed',
          successCount,
          failCount,
          allSuccess
        });
      }

      return {
        success: allSuccess,
        successCount,
        failCount,
        pendingCount: await db.getPendingCount()
      };

    } catch (error) {
      console.error('❌ Sync process error:', error);
      if (!this.silentMode) {
        this.notify({ type: 'sync_error', error: error.message });
      }
      return { success: false, error: error.message };
    } finally {
      this.isSyncing = false;
      this.silentMode = false; // Reset
    }
  }

  /**
   * Build dependency chains from transactions
   * Groups related transactions (order → payment) together
   */
  buildDependencyChains(transactions) {
    const chains = [];
    const processed = new Set();
    
    // Sort by priority first, then by timestamp
    const sorted = [...transactions].sort((a, b) => {
      if (a.priority !== b.priority) {
        return a.priority - b.priority;
      }
      return new Date(a.createdAt) - new Date(b.createdAt);
    });

    for (const txn of sorted) {
      if (processed.has(txn.transactionId)) continue;

      const chain = [txn];
      processed.add(txn.transactionId);

      // Find related transactions (e.g., payment for this order)
      if (this.isOrderCreation(txn.endpoint)) {
        const relatedPayments = sorted.filter(t => 
          !processed.has(t.transactionId) &&
          this.isPayment(t.endpoint) &&
          this.isRelated(txn, t)
        );

        for (const payment of relatedPayments) {
          chain.push(payment);
          processed.add(payment.transactionId);
        }
        
        // Also find cancellations
        const relatedCancels = sorted.filter(t => 
          !processed.has(t.transactionId) &&
          this.isCancellation(t.endpoint) &&
          this.isRelated(txn, t)
        );
        
        for (const cancel of relatedCancels) {
          chain.push(cancel);
          processed.add(cancel.transactionId);
        }
      }

      chains.push(chain);
    }

    return chains;
  }

  /**
   * Check if two transactions are related (same order)
   */
  isRelated(txn1, txn2) {
    // Check if txn2 has dependsOn field that matches txn1
    if (txn2.dependsOn && txn2.dependsOn === txn1.transactionId) {
      console.log(`  🔗 Found related transaction via dependsOn: ${txn2.transactionId} depends on ${txn1.transactionId}`);
      return true;
    }
    
    // Fallback: Payment/cancel transaction references order ID in data
    if (this.isPayment(txn2.endpoint) || this.isCancellation(txn2.endpoint)) {
      try {
        const requestData = typeof txn2.data === 'string' ? JSON.parse(txn2.data) : txn2.data;
        const orderId = requestData?.orderId || requestData?.id;
        if (orderId === txn1.transactionId) {
          console.log(`  🔗 Found related transaction via orderId: ${txn2.transactionId} references order ${orderId}`);
          return true;
        }
      } catch (e) {
        console.warn('  ⚠️ Could not parse transaction data for relation check:', e);
      }
    }
    return false;
  }

  /**
   * Apply ID mappings to transaction data
   */
  applyIdMappings(txn, idMappings) {
    if (!this.isPayment(txn.endpoint) && !this.isCancellation(txn.endpoint)) {
      return txn;
    }

    // Parse data if it's a string
    let txnData = txn.data;
    if (typeof txnData === 'string') {
      try {
        txnData = JSON.parse(txnData);
      } catch (e) {
        console.warn('  ⚠️ Could not parse transaction data:', e);
        return txn;
      }
    }

    const offlineOrderId = txnData?.orderId || txnData?.id;
    console.log(`  🔍 Looking for ID mapping: offlineOrderId=${offlineOrderId}, dependsOn=${txn.dependsOn}`);
    console.log(`  🔍 Available mappings:`, Object.fromEntries(idMappings));
    
    const serverOrderId = idMappings.get(offlineOrderId);

    if (serverOrderId) {
      console.log(`  🔄 Applying ID mapping: ${offlineOrderId} → ${serverOrderId}`);
      const updatedData = {
        ...txnData,
        orderId: serverOrderId,
        id: serverOrderId
      };
      return {
        ...txn,
        data: updatedData  // Return as object, axios will handle serialization
      };
    }

    // Also check dependsOn field for mapping
    if (txn.dependsOn && idMappings.has(txn.dependsOn)) {
      const serverOrderId = idMappings.get(txn.dependsOn);
      console.log(`  🔄 Applying ID mapping via dependsOn: ${txn.dependsOn} → ${serverOrderId}`);
      const updatedData = {
        ...txnData,
        orderId: serverOrderId,
        id: serverOrderId
      };
      return {
        ...txn,
        data: updatedData
      };
    }

    return txn;
  }

  /**
   * Sync a single transaction
   */
  async syncTransaction(txn) {
    // Log the data being sent for debugging
    const dataToSend = typeof txn.data === 'string' ? JSON.parse(txn.data) : txn.data;
    console.log(`  📤 Sending to ${txn.endpoint}:`, {
      method: txn.method,
      dataKeys: dataToSend ? Object.keys(dataToSend) : [],
      branchId: dataToSend?.branchId || dataToSend?.branchPk,
      orderId: dataToSend?.orderId || dataToSend?._id
    });
    
    // Make the API request
    // Mark as sync retry so interceptor doesn't re-queue on failure
    // IMPORTANT: Use fresh token from localStorage, not the stale token stored with the transaction.
    // The stored token may have expired during the offline period.
    const freshToken = localStorage.getItem('token');
    const freshHeaders = {
      ...(txn.headers || {}),
      ...(freshToken ? { 'Authorization': `Bearer ${freshToken}` } : {})
    };
    
    const response = await axios({
      method: txn.method,
      url: txn.endpoint,
      data: txn.data,
      headers: freshHeaders,
      params: txn.params,
      timeout: 30000,
      _isSyncRetry: true // Prevent re-queuing if this fails
    });

    console.log(`  📥 Response from ${txn.endpoint}:`, {
      status: response.status,
      hasData: !!response.data,
      dataId: response.data?._id || response.data?.data?._id
    });

    return response;
  }

  /**
   * Handle successful sync
   */
  async handleSyncSuccess(txn, response, isLastInChain = false) {
    // Delete transaction from queue
    await db.deleteTransaction(txn.transactionId);
    console.log(`  ✅ Transaction synced and removed from queue`);

    // Handle order creation syncing
    if (this.isOrderCreation(txn.endpoint)) {
      try {
        // ALWAYS delete the offline order immediately to prevent duplicates
        const offlineOrder = await db.ordersCache.get(txn.transactionId);
        if (offlineOrder) {
          await db.ordersCache.delete(txn.transactionId);
          console.log(`  🗑️ Deleted offline order: ${txn.transactionId} (replaced with server data)`);
        }

        // Cache the server order (replacement)
        const serverOrder = response.data?.data || response.data;
        if (serverOrder && serverOrder._id) {
          await db.cacheOrder({
            ...serverOrder,
            offlineOrder: false,
            syncStatus: 'synced'
          });
          console.log(`  💾 Cached server order: ${serverOrder._id} (replaces offline order)`);
        }
      } catch (error) {
        console.warn(`  ⚠️ Error replacing offline order with server data:`, error);
      }
    }
    
    // Handle payment syncing - delete any cached version and replace with paid server order
    if (this.isPayment(txn.endpoint)) {
      try {
        // Get the server order ID from response (payment API returns it directly in data._id)
        const serverOrderId = response.data?._id || response.data?.data?._id;
        console.log(`  🔍 Payment sync - Server order ID: ${serverOrderId}`);
        
        // Get the offline order ID from request
        const requestData = typeof txn.data === 'string' ? JSON.parse(txn.data) : txn.data;
        const offlineOrderId = requestData?.orderId || requestData?.id;
        console.log(`  🔍 Payment sync - Offline order ID from request: ${offlineOrderId}`);
        
        // Delete the offline order using the offline ID
        if (offlineOrderId) {
          const offlineOrder = await db.ordersCache.get(offlineOrderId);
          if (offlineOrder) {
            await db.ordersCache.delete(offlineOrderId);
            console.log(`  🗑️ Deleted offline order: ${offlineOrderId}`);
          }
        }
        
        // Also delete using server ID if different
        if (serverOrderId && serverOrderId !== offlineOrderId) {
          const cachedOrder = await db.ordersCache.get(serverOrderId);
          if (cachedOrder) {
            await db.ordersCache.delete(serverOrderId);
            console.log(`  🗑️ Deleted cached order: ${serverOrderId}`);
          }
        }
        
        // Cache the paid order from server response
        // Payment API returns the full order data in response.data
        const paidOrder = response.data?.data || response.data;
        if (paidOrder && (paidOrder._id || serverOrderId)) {
          const orderToCache = {
            ...paidOrder,
            _id: paidOrder._id || serverOrderId,
            offlineOrder: false,
            syncStatus: 'synced',
            isPaid: true,
            paymentStatus: 'PAID',
            ordStatus: 'COMPLETED'
          };
          await db.cacheOrder(orderToCache);
          console.log(`  💾 Cached paid order from server: ${orderToCache._id}`);
        } else {
          console.warn(`  ⚠️ Payment sync: Could not cache paid order - no order data in response`);
        }
      } catch (error) {
        console.warn(`  ⚠️ Error cleaning up cached order after payment:`, error);
      }
    }
  }

  /**
   * Handle sync error
   */
  async handleSyncError(txn, error) {
    const status = error.response?.status;

    if (status === 409) {
      // Conflict - log for manual resolution
      await db.logConflict({
        transactionId: txn.transactionId,
        conflictType: 'duplicate_or_conflict',
        data: txn,
        serverResponse: error.response?.data,
        timestamp: new Date().toISOString()
      });
      await db.updateTransactionStatus(txn.transactionId, 'conflict');
      console.log(`  ⚠️ Conflict detected and logged`);
    } else if (status >= 400 && status < 500) {
      // Client error - don't retry, delete from queue
      const serverErrorMessage = error.response?.data?.message || error.response?.data?.error || error.response?.data || 'Unknown error';
      console.error(`  ❌ Client error ${status} - Server message:`, serverErrorMessage);
      console.error(`  ❌ Request data:`, typeof txn.data === 'string' ? JSON.parse(txn.data) : txn.data);
      
      await db.logConflict({
        transactionId: txn.transactionId,
        conflictType: 'client_error',
        data: txn,
        error: error.message,
        serverMessage: serverErrorMessage,
        status: status,
        timestamp: new Date().toISOString()
      });
      // Delete transaction instead of marking as failed to prevent retry loop
      await db.deleteTransaction(txn.transactionId);
      console.log(`  ❌ Client error ${status} - deleted from queue (endpoint: ${txn.endpoint})`);
    } else {
      // Server error or network error - retry
      await db.incrementRetryCount(txn.transactionId);
      console.log(`  🔄 Will retry (attempt ${txn.retryCount + 1}/${this.maxRetries})`);
    }
  }

  /**
   * Extract server-assigned ID from response
   */
  extractServerId(response) {
    return response.data?._id || response.data?.data?._id || null;
  }

  /**
   * Check if endpoint is order creation
   */
  isOrderCreation(endpoint) {
    return endpoint.includes('addPosOrder') || 
           endpoint.includes('postOrderFromFloorPlan') ||
           endpoint.includes('addQuickBill');
  }

  /**
   * Check if endpoint is payment
   */
  isPayment(endpoint) {
    return endpoint.includes('postPosPayment');
  }
  
  /**
   * Check if endpoint is cancellation
   */
  isCancellation(endpoint) {
    return endpoint.includes('cancelOngoingOrders');
  }
  
  /**
   * Check if endpoint is a read operation (should not be queued for sync)
   */
  isReadOperation(endpoint) {
    const readEndpoints = [
      'listCompletedSelfOrder',
      'listPosOrders',
      'stockreport',
      'viewReservationList',
      'viewReservation',
      'getProducts',
      'getCategories',
      'getBranchConfiguration',
      // Report/account endpoints - read-only, must never be queued or synced
      'accountReceivableAgingReport',
      'accountPayableAgingReport',
      'singleCustomerARAR',
      'accountReceivableReport',
      'accountPayableReport',
      'agingReport',
      'inOutVatReport',
      'listTimeReport',
      'shiftReportSale',
      'shiftReportProducts',
      'shiftReportPaymentSummary',
      'shiftSummeryReport',
      'salesDetailsReport',
      'aggregatorReport',
      'viewExpenseReport',
      'viewlog',
      'getlog',
    ];
    return readEndpoints.some(e => endpoint.includes(e));
  }

  /**
   * Clear order cache to force refresh
   */
  async clearOrderCache() {
    try {
      const allCacheKeys = await db.apiCache.toArray();
      const orderCacheKeys = allCacheKeys.filter(item => 
        item.url && item.url.includes('listPosOrders')
      );
      
      for (const cacheItem of orderCacheKeys) {
        await db.apiCache.delete(cacheItem.url);
      }
      
      console.log(`🗑️ Cleared ${orderCacheKeys.length} order cache entries`);
    } catch (error) {
      console.warn('Could not clear cache:', error);
    }
  }

  /**
   * Delay helper
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get sync status
   */
  async getStatus() {
    const pendingCount = await db.getPendingCount();
    const conflicts = await db.conflictLog.toArray();
    
    return {
      isSyncing: this.isSyncing,
      pendingCount,
      conflictCount: conflicts.length,
      isOnline: navigator.onLine
    };
  }

  /**
   * Retry failed transactions
   */
  async retryFailed() {
    const failed = await db.transactionQueue
      .where('status')
      .equals('failed')
      .toArray();

    for (const txn of failed) {
      await db.updateTransactionStatus(txn.transactionId, 'pending');
      await db.transactionQueue
        .where('transactionId')
        .equals(txn.transactionId)
        .modify({ retryCount: 0 });
    }

    console.log(`🔄 Reset ${failed.length} failed transactions to pending`);
    
    return this.syncAll();
  }

  /**
   * Sync pending shift end requests
   * These must be synced FIRST before other transactions
   */
  async syncPendingShiftEnds() {
    try {
      const pendingShiftEnds = await db.getPendingShiftEnds();
      
      if (pendingShiftEnds.length === 0) {
        console.log('✅ No pending shift ends to sync');
        return { success: true, hasPending: false };
      }

      console.log(`🔔 Found ${pendingShiftEnds.length} pending shift end(s) to sync`);
      
      for (const shiftEnd of pendingShiftEnds) {
        console.log(`  🔄 Syncing shift end: ${shiftEnd.shiftId}`);
        
        try {
          // Get the original request body
          const requestBody = shiftEnd.requestBody || {
            branchId: shiftEnd.branchId,
            shiftId: shiftEnd.shiftId,
            endDate: shiftEnd.endDate,
            empId: shiftEnd.empId,
            posDeviceId: shiftEnd.posDeviceId,
            posDeviceName: shiftEnd.posDeviceName
          };

          // Make the API call
          const response = await axios.post('settings/endmanualshift', requestBody, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            timeout: 60000 // 60 seconds - shift end can take time
          });

          if (response.status === 200) {
            console.log(`  ✅ Shift end synced successfully: ${shiftEnd.shiftId}`);
            
            // Remove from queue
            await db.removeShiftEnd(shiftEnd.shiftId);
            
            // Clear local shift status
            await db.clearLocalShiftStatus(shiftEnd.branchId);
            
            // Clear shiftStatus from localStorage
            localStorage.removeItem('shiftStatus');
          }
        } catch (error) {
          console.error(`  ❌ Failed to sync shift end ${shiftEnd.shiftId}:`, error);
          
          // Update status to failed
          await db.updateShiftEndStatus(shiftEnd.shiftId, 'failed', error);
          
          // Check if it's a validation error (4xx) - don't retry these
          if (error.response?.status >= 400 && error.response?.status < 500) {
            console.log(`  ⚠️ Shift end rejected by server: ${error.response?.data}`);
            // Remove from queue - server rejected it
            await db.removeShiftEnd(shiftEnd.shiftId);
            await db.clearLocalShiftStatus(shiftEnd.branchId);
            localStorage.removeItem('shiftStatus');
            continue; // Try next shift end
          }
          
          // Network/server error - stop and report failure
          return { 
            success: false, 
            hasPending: true, 
            error: error.message,
            shiftId: shiftEnd.shiftId
          };
        }
      }

      console.log('✅ All pending shift ends synced successfully');
      return { success: true, hasPending: false };
      
    } catch (error) {
      console.error('❌ Error in syncPendingShiftEnds:', error);
      return { success: false, hasPending: true, error: error.message };
    }
  }

  /**
   * Sync a specific offline order by its UUID
   * Returns the server-assigned MongoDB _id if successful
   * @param {string} offlineOrderId - The UUID of the offline order
   * @returns {Promise<{success: boolean, serverId?: string, error?: string}>}
   */
  async syncOfflineOrderById(offlineOrderId) {
    if (!offlineOrderId) {
      return { success: false, error: 'No order ID provided' };
    }

    // Check if this is a UUID (offline order) or MongoDB ObjectId
    const isUUID = offlineOrderId.includes('-') && offlineOrderId.length === 36;
    if (!isUUID) {
      // Already a MongoDB ObjectId, no sync needed
      console.log(`✅ Order ${offlineOrderId} is already a server ID, no sync needed`);
      return { success: true, serverId: offlineOrderId };
    }

    console.log(`🔄 Syncing offline order: ${offlineOrderId}`);

    try {
      // Find the transaction for this order
      const transactions = await db.getPendingTransactions();
      const orderTxn = transactions.find(txn => 
        txn.transactionId === offlineOrderId && this.isOrderCreation(txn.endpoint)
      );

      if (!orderTxn) {
        console.log(`⚠️ No pending transaction found for order: ${offlineOrderId}`);
        // Check if order exists in cache (might already be synced)
        const cachedOrder = await db.ordersCache.get(offlineOrderId);
        if (cachedOrder && cachedOrder.syncStatus === 'synced') {
          return { success: true, serverId: cachedOrder.serverOrderId || offlineOrderId };
        }
        return { success: false, error: 'Order transaction not found in queue' };
      }

      console.log(`📤 Found order transaction, syncing to server...`);

      // Sync the order transaction
      const response = await this.syncTransaction(orderTxn);
      
      // Extract server ID from response
      const serverId = this.extractServerId(response);
      
      if (serverId) {
        console.log(`✅ Order synced successfully: ${offlineOrderId} → ${serverId}`);
        
        // Handle sync success (delete from queue, update cache)
        await this.handleSyncSuccess(orderTxn, response, false);
        
        // Also update any payment transactions that depend on this order
        const paymentTxns = transactions.filter(txn => 
          txn.dependsOn === offlineOrderId && this.isPayment(txn.endpoint)
        );
        
        for (const paymentTxn of paymentTxns) {
          // Update the orderId in the payment transaction
          const paymentData = typeof paymentTxn.data === 'string' 
            ? JSON.parse(paymentTxn.data) 
            : paymentTxn.data;
          
          paymentData.orderId = serverId;
          
          await db.transactionQueue
            .where('transactionId')
            .equals(paymentTxn.transactionId)
            .modify({ data: JSON.stringify(paymentData) });
          
          console.log(`📝 Updated payment transaction with server ID: ${serverId}`);
        }
        
        return { success: true, serverId };
      } else {
        console.error(`❌ Failed to extract server ID from response`);
        return { success: false, error: 'Failed to get server ID' };
      }
      
    } catch (error) {
      console.error(`❌ Failed to sync offline order:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Check if an order ID is an offline UUID
   * @param {string} orderId 
   * @returns {boolean}
   */
  isOfflineOrderId(orderId) {
    if (!orderId || typeof orderId !== 'string') return false;
    // UUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx (36 chars with 4 dashes)
    return orderId.includes('-') && orderId.length === 36;
  }
}

// Export singleton instance
const syncEngine = new SyncEngine();
export default syncEngine;